import React, { useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spinner, Text } from '@fluentui/react-components';
import { oktaConfig } from '../config/oktaConfig';

const Login = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  
  // Get the intended destination from location state, or default to home
  const fromUri = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    const handleLogin = async () => {
      // Add check for oktaAuth
      if (!oktaAuth) {
        setError('Okta authentication context is not available. Please ensure you have wrapped your app with OktaAuth provider.');
        return;
      }

      if (!authState) return; // Wait for authState to be available

      if (authState?.isAuthenticated) {
        // If user is already authenticated, redirect to intended destination
        navigate(fromUri, { replace: true });
      } else {
        try {

          // Initiate the login redirect using oktaConfig settings
          await oktaAuth.signInWithRedirect({
            originalUri: fromUri,
            scopes: oktaConfig.scopes
          });
        } catch (error) {
          console.error('Login redirect error:', error);
          setError(error instanceof Error ? error.message : 'An unknown error occurred');
          
          // Additional debug information
          if (error instanceof Error) {
            console.debug('Error details:', {
              name: error.name,
              message: error.message,
              stack: error.stack
            });
          }
        }
      }
    };

    handleLogin();
  }, [authState, oktaAuth, fromUri, navigate]);

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <Text weight="semibold" color="red">
          Authentication Error
        </Text>
        <Text>
          {error}
        </Text>
        <Text size={100}>
          Please check your console for more details or contact support.
        </Text>
      </div>
    );
  }

  // Show loading spinner while handling redirect
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column',
      gap: '1rem'
    }}>
      <Spinner size="large" />
      <div>Redirecting to login...</div>
    </div>
  );
};

export default Login; 